import classnames from 'classnames';
import { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { helpCenterActions } from 'state/helpRequest/actions';
import { getUserPreferenceShowBatchShortcutCheatsheet, isUserPreferencesDarkMode } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import { userPreferencesNeverShowBatchWorkspaceShortcutCheatsheetBannerChanged } from 'state/userPreferences/userPreferences.action';
import { batchWorkspaceShortcutCheatsheetBannerClosed } from 'state/workspaces/batch/shortcutCheatsheetBanner/shortcutCheatsheetBanner.actions';
import { getIsCheatsheetBannerClosed } from 'state/workspaces/batch/shortcutCheatsheetBanner/shortcutCheatsheetBanner.selectors';
import styled from 'styled-components';

export const ShortcutCheatSheetBanner: FC = () => {
  const trackEvent = 'Help Center';
  const { formatMessage } = useIntl();
  const dispatch = useDispatch<ThunkDispatch>();

  const userPreferenceShowBatchShortcutCheatsheet = useSelector(getUserPreferenceShowBatchShortcutCheatsheet);
  const isCheatsheetBannerClosed = useSelector(getIsCheatsheetBannerClosed);
  const isDarkMode = useSelector(isUserPreferencesDarkMode);

  const [displayNeverShowReminderAlertBox, setDisplayNeverShowReminderAlertBox] = useState(false);

  const triggerNeverShowReminderAlertBox = useCallback(() => {
    setDisplayNeverShowReminderAlertBox(true);
  }, []);

  useEffect(() => {
    if (displayNeverShowReminderAlertBox) {
      const timerId = setTimeout(function () {
        setDisplayNeverShowReminderAlertBox(false);
      }, 5000);
      return clearTimeout(timerId);
    }
  }, [displayNeverShowReminderAlertBox]);

  const dismissShortcutBanner = useCallback(() => {
    logAnalytics.trackEvent(trackEvent, 'Dismiss keyboard shortcut banner');
    dispatch(batchWorkspaceShortcutCheatsheetBannerClosed());
  }, [dispatch]);

  const StyledCardDiv = styled.div`
    background-color: ${isDarkMode ? 'rgb(69, 64, 63)' : 'rgb(245, 243, 243)'};
    overflow: 'hidden';
  `;

  const StyledCardFooterDiv = styled.div`
    background-color: ${isDarkMode ? '' : 'rgb(245, 243, 243)'};
  `;

  const showShortcutCheatsheetBanner = userPreferenceShowBatchShortcutCheatsheet && !isCheatsheetBannerClosed;

  return (
    <>
      {showShortcutCheatsheetBanner && (
        <div
          className="pe-4 mb-5 d-flex justify-content-end"
          style={{ position: 'absolute', bottom: '0px', right: '0px', zIndex: 6, maxWidth: '372px' }}
        >
          <div className="col-12 col-md-6 col-xl-4 w-100">
            <StyledCardDiv className="card shadow-max h-100">
              <div className="card-header d-flex p-1">
                <div className="d-flex align-items-center flex-grow-1 ps-2">
                  <i className="icon text-info">help</i>
                </div>
                <div className="d-block p-1">
                  <a
                    className="me-1"
                    onClick={() => {
                      triggerNeverShowReminderAlertBox();
                      logAnalytics.trackEvent(trackEvent, 'Never show keyboard shortcut banner again');
                      dispatch(userPreferencesNeverShowBatchWorkspaceShortcutCheatsheetBannerChanged());
                    }}
                  >
                    {formatMessage({ id: 'batchPricer.banner.keyboard.shortcut.dontShowAgain' })}
                  </a>
                  <button
                    type="button"
                    className="btn btn-flat-secondary btn-icon"
                    aria-label="Close"
                    onClick={() => dismissShortcutBanner()}
                  >
                    <i className="icon icon-md">close</i>
                  </button>
                </div>
              </div>
              <div className="card-body d-flex flex-column justify-content-end gap-1 p-3">
                <p className="h6" style={{ lineHeight: 1.3 }}>
                  {formatMessage({ id: 'batchPricer.banner.keyboard.shortcut.title' })}
                </p>
              </div>
              <StyledCardFooterDiv
                className={classnames(
                  'card-footer p-2 d-flex justify-content-end',
                  isDarkMode ? 'border-opacity-40 border-secondary' : 'border-opacity-70',
                )}
              >
                <button
                  className={classnames(
                    'text-nowrap btn btn-flat-secondary',
                    isDarkMode
                      ? 'btn-flat-light border-secondary border-opacity-40'
                      : 'btn-flat-dark border-opacity-70',
                  )}
                  onClick={() => dismissShortcutBanner()}
                >
                  {formatMessage({ id: 'dismiss' })}
                </button>
                <button
                  className="text-nowrap btn btn-flat-primary btn-icon-end"
                  onClick={() => {
                    logAnalytics.trackEvent(trackEvent, 'Open keyboard shortcut');
                    dispatch(helpCenterActions.openTopic('batch.pricing.keyboardNavigation'));
                  }}
                >
                  {formatMessage({ id: 'batchPricer.banner.keyboard.shortcut.seeShortcuts' })}{' '}
                  <i className="icon">open_in_new</i>
                </button>
              </StyledCardFooterDiv>
            </StyledCardDiv>
          </div>
        </div>
      )}
      {displayNeverShowReminderAlertBox && (
        <div
          className="alert position-fixed text-center feedback-alert alert-secondary text-primary-alt fadeOut"
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            zIndex: 6,
            transform: 'translate(-50%, -50%)',
            transition: 'opacity 0.5s',
            opacity: displayNeverShowReminderAlertBox ? 1 : 0,
          }}
        >
          We will not show this reminder again. Find it anytime in Help center.
        </div>
      )}
    </>
  );
};
